/**
 * Analysera Trippar (admin)
 * Jämför registrerade trippar (MongoDB) mot vad fordonets bin-fil skulle ge, för att
 * identifiera avvikelser. Read-only (fas A). Replay/reparation (fas B) gated på REPLAY-behörighet.
 */
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, MapIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useTranslation } from '@/store/languageStore';
import { usePermissionsStore } from '@/store/permissionsStore';
import { ROUTES } from '@/constants/routes';
import OrgTreeSelect from '@/components/common/OrgTreeSelect';
import SearchableVehicleSelect from '@/components/vehicle/SearchableVehicleSelect';
import TripListPanel from '@/components/trips/TripListPanel';
import { useTripAnalysis } from '@/hooks/useTripAnalysis';

import DateInput from '@/components/common/DateInput';

const todayLocal = (): string => {
  const d = new Date();
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60000).toISOString().slice(0, 10);
};

const TripAnalysisPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const canReplay = usePermissionsStore((s) => s.hasActionAccess('ADMIN_TRIP_ANALYSIS', 'REPLAY'));

  const [organizationFilter, setOrganizationFilter] = useState('');
  const [vehicleId, setVehicleId] = useState('');
  const [fromDate, setFromDate] = useState(todayLocal());
  const [toDate, setToDate] = useState(todayLocal());
  const [replayLoading, setReplayLoading] = useState(false);

  const {
    registeredTrips,
    binTrips,
    binResult,
    loadingRegistered,
    loadingBin,
    error,
    binError,
    loadRegistered,
    loadBin,
    runReplay,
  } = useTripAnalysis();

  const handleAnalyze = () => {
    if (!vehicleId) {
      toast.error(t('tripAnalysis.selectVehicleFirst') );
      return;
    }
    loadRegistered(vehicleId, fromDate, toDate);
    loadBin(vehicleId, fromDate, toDate);
  };

  const handleReplay = async () => {
    if (!vehicleId) return;
    if (underDetected) {
      const ok = window.confirm(
        t('tripAnalysis.replayDestructiveConfirm') 
      );
      if (!ok) return;
    }
    setReplayLoading(true);
    try {
      await runReplay(vehicleId, fromDate, toDate);
      toast.success(t('tripAnalysis.replayStarted') );
    } catch (e: any) {
      toast.error(e?.message || t('tripAnalysis.replayFailed') );
    } finally {
      setReplayLoading(false);
    }
  };

  const rawStats = binResult?.rawStats;

  const deviation = useMemo(() => {
    if (!binResult) return null;
    const registeredCount = registeredTrips.length;
    const binCount = binTrips.length;
    return { registeredCount, binCount, diff: binCount - registeredCount };
  }, [binResult, registeredTrips.length, binTrips.length]);

  // Bin-filen har körning (hastighet) men batch/replay-detektering gav färre trippar än de
  // registrerade -> detekteringsalgoritmen under-segmenterar (t.ex. FMM003 med gles AVL250).
  // Replay vore då destruktivt (skulle ersätta korrekta trippar med färre).
  const underDetected = useMemo(() => {
    if (!binResult) return false;
    return (
      registeredTrips.length > 0 &&
      binTrips.length < registeredTrips.length &&
      (rawStats?.speedMaxKmh ?? 0) > 0
    );
  }, [binResult, registeredTrips.length, binTrips.length, rawStats]);

  const warnings = useMemo(() => {
    const w: string[] = [];
    if (binResult && binResult.keptFileCount === 0) {
      w.push(t('tripAnalysis.warnNoBinFile') );
      return w;
    }
    if (!rawStats) return w;
    if (rawStats.records === 0) {
      w.push(t('tripAnalysis.warnNoRecords') );
      return w;
    }
    if (rawStats.speedMaxKmh === 0) {
      w.push(t('tripAnalysis.warnNoSpeed') );
    }
    if (rawStats.avl250Count === 0) {
      w.push(t('tripAnalysis.warnNoAvl250') );
    }
    if (rawStats.satZeroPct >= 50) {
      w.push(
        (t('tripAnalysis.warnNoGpsFix') ) + ` (${rawStats.satZeroPct}%)`
      );
    }
    if (rawStats.medianIntervalSec > 120) {
      w.push(
        (t('tripAnalysis.warnSparse') ) +
          ` (median ${rawStats.medianIntervalSec}s)`
      );
    }
    return w;
  }, [binResult, rawStats, t]);

  return (
    <div className="p-6 bg-bgContainer rounded-lg h-full flex flex-col overflow-hidden">
      <div className="flex items-center gap-4 mb-6 shrink-0">
        <button
          onClick={() => navigate(ROUTES.SYSTEM_ADMINISTRATION)}
          className="p-2 hover:bg-bgElevated rounded-lg transition-colors"
        >
          <ArrowLeftIcon className="w-5 h-5 text-textDefault" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-textDefault flex items-center gap-2">
            <MapIcon className="w-6 h-6 text-primary" />
            {t('tripAnalysis.title') }
          </h1>
          <p className="text-sm text-textSecondary">
            {t('tripAnalysis.description') }
          </p>
        </div>
      </div>

      {/* Filterrad */}
      <div className="bg-bgElevated shadow rounded-lg p-4 mb-4 shrink-0">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
          <div>
            <label className="block text-sm font-medium text-textDefault mb-2">
              {t('fleet.organization') }
            </label>
            <OrgTreeSelect
              value={organizationFilter}
              onChange={(orgId) => {
                setOrganizationFilter(orgId);
                setVehicleId('');
              }}
              showEmptyOption
              emptyLabel={t('fleet.filterAllOrganizations') }
              className="w-full"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-textDefault mb-2">
              {t('trips.selectVehicle') }
            </label>
            <SearchableVehicleSelect
              organizationId={organizationFilter || undefined}
              selectedValue={vehicleId}
              onChange={setVehicleId}
              placeholder={t('trips.selectVehiclePlaceholder') }
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs text-textSecondary mb-1">
                {t('trips.fromDate') }
              </label>
              <DateInput value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="w-full rounded border border-border bg-bgBase text-textDefault px-2 py-1.5 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs text-textSecondary mb-1">
                {t('trips.toDate') }
              </label>
              <DateInput value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="w-full rounded border border-border bg-bgBase text-textDefault px-2 py-1.5 text-sm"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleAnalyze}
              disabled={!vehicleId || loadingBin || loadingRegistered}
              className="flex-1 px-4 py-2 text-sm font-medium bg-primary text-white rounded hover:bg-primary/90 disabled:opacity-50"
            >
              {t('tripAnalysis.analyze') }
            </button>
            {canReplay && (
              <button
                type="button"
                onClick={handleReplay}
                disabled={!vehicleId || replayLoading}
                title={t('tripAnalysis.replayHint') }
                className="px-4 py-2 text-sm font-medium bg-bgBase text-textDefault border border-border rounded hover:bg-bgHover disabled:opacity-50"
              >
                {replayLoading ? t('common.loading')  : t('tripAnalysis.replay') }
              </button>
            )}
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 rounded bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm shrink-0">
          {error}
        </div>
      )}

      {/* Avvikelse-banner */}
      {binResult && (
        <div className="mb-4 bg-bgElevated shadow rounded-lg p-4 shrink-0">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
            <span className="text-textDefault">
              <strong>{t('tripAnalysis.registered') }:</strong> {deviation?.registeredCount ?? 0}
            </span>
            <span className="text-textDefault">
              <strong>{t('tripAnalysis.fromBin') }:</strong> {deviation?.binCount ?? 0}
            </span>
            <span className={`font-medium ${deviation && deviation.diff !== 0 ? 'text-amber-600 dark:text-amber-400' : 'text-green-600 dark:text-green-400'}`}>
              {t('tripAnalysis.diff') }: {deviation ? (deviation.diff > 0 ? `+${deviation.diff}` : deviation.diff) : 0}
            </span>
            {binResult.imei && <span className="text-textSecondary">IMEI: {binResult.imei}</span>}
            {rawStats && (
              <span className="text-textSecondary">
                {rawStats.records} {t('tripAnalysis.records') } • max {rawStats.speedMaxKmh} km/h •
                {' '}AVL250: {rawStats.avl250Count} • sat=0: {rawStats.satZeroPct}%
              </span>
            )}
          </div>
          {warnings.length > 0 && (
            <div className="mt-3 flex flex-col gap-1">
              {warnings.map((w, i) => (
                <div key={i} className="flex items-center gap-2 text-sm text-amber-700 dark:text-amber-300">
                  <ExclamationTriangleIcon className="w-4 h-4 shrink-0" />
                  <span>{w}</span>
                </div>
              ))}
            </div>
          )}
          {binError && !warnings.length && (
            <div className="mt-2 text-sm text-amber-700 dark:text-amber-300">{binError}</div>
          )}
          {underDetected && (
            <div className="mt-3 p-3 rounded border border-red-300 bg-red-50 dark:border-red-800 dark:bg-red-900/20">
              <div className="flex items-start gap-2 text-sm text-red-700 dark:text-red-300">
                <ExclamationTriangleIcon className="w-5 h-5 shrink-0 mt-0.5" />
                <div>
                  <div className="font-semibold">
                    {t('tripAnalysis.underDetectedTitle') }
                  </div>
                  <div className="mt-1">
                    {t('tripAnalysis.underDetectedBody') }
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Två kolumner: registrerade vs bin */}
      <div className="flex-1 grid grid-cols-1 lg:grid-cols-2 gap-6 min-h-0">
        <TripListPanel
          title={t('tripAnalysis.registeredTrips') }
          trips={registeredTrips}
          variant="registered"
          loading={loadingRegistered}
          emptyLabel={
            vehicleId
              ? t('tripAnalysis.noRegistered') 
              : t('tripAnalysis.selectVehicleFirst') 
          }
        />
        <TripListPanel
          title={t('tripAnalysis.binTrips') }
          trips={binTrips}
          variant="bin"
          loading={loadingBin}
          emptyLabel={
            vehicleId
              ? t('tripAnalysis.noBinTrips') 
              : t('tripAnalysis.loadBinHint') 
          }
        />
      </div>
    </div>
  );
};

export default TripAnalysisPage;
